import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "../services/api";
import { fetchProviders } from "../services/providerService";
import { param, useListUrlState, yesNo } from "./useUrlState";

const FILTERS = {
  search: param.string("", { debounce: 400 }),
  branch: param.string(),
  service: param.string(),
  employmentStatus: param.oneOf(["active", "leave", "inactive"]),
  isBookableOnline: yesNo(),
};

/**
 * The business's service providers, with the list's paging and filter state.
 * `reload()` refetches the current page — mutations call it so the table and
 * the counts stay in step with the API.
 */
export default function useProviders() {
  const { t, i18n } = useTranslation();

  const [providers, setProviders] = useState([]);
  const [meta, setMeta] = useState(null);
  // Page, filters and search live in the URL — see useUrlState.
  const list = useListUrlState(FILTERS);
  const { page, filters } = list;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { providers: list, meta: pagination } = await fetchProviders({ page, filters });
      setProviders(list);
      setMeta(pagination);
    } catch (err) {
      setError(apiErrorMessage(err, t("providers.loadFailed")));
      setProviders([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [page, filters, i18n.language, t]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    providers,
    meta,
    page,
    setPage: list.setPage,
    filters,
    applyFilters: list.applyFilters,
    resetFilters: list.resetFilters,
    searchTerm: list.searchTerm,
    setSearchTerm: list.setSearchTerm,
    submitSearch: list.submitSearch,
    loading,
    error,
    reload: load,
  };
}
